import moment from "moment";
import type { CsvType } from "../types";
import decodeHtmlEntities from "./decode-html-entities";

const HEADERS = [
  "Story Name",
  "Version Name",
  "Created At",
  "Reader",
  "Session Index",
  "Session Started",
  "Duration",
  "Progress",
  "Clicked CTA",
  "Location",
  "Device",
  "Reader Email",
  "Reader Position",
] as const;

const mapData = (data: CsvType[]) => {
  // Sort data by version first, then by timestamp
  const sortedData = [...data].sort((a, b) => {
    // First sort by version
    const versionCompare = a.version_title.localeCompare(b.version_title);
    if (versionCompare !== 0) return versionCompare;
    // Then by timestamp
    return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
  });

  // Create a map to track visitor index per version
  const versionVisitorMap = new Map<string, Map<string, number>>();

  // First pass: assign visitor indices per version
  sortedData.forEach((item) => {
    if (!versionVisitorMap.has(item.version_title)) {
      versionVisitorMap.set(item.version_title, new Map());
    }

    const visitorMap = versionVisitorMap.get(item.version_title)!;
    if (!visitorMap.has(item.visitor_id)) {
      // Assign the next index for this version (starting from 1)
      visitorMap.set(item.visitor_id, visitorMap.size + 1);
    }
  });

  return sortedData.map((item) => {
    // Calculate progress percentage like in the UI
    const progressValue = (item.last_slide / item.slides_count) * 100;
    const progress = Number.isInteger(progressValue)
      ? `${progressValue}%`
      : `${Number(progressValue.toFixed(1))}%`;

    // Get the version-specific visitor index
    const versionVisitorIndex = versionVisitorMap
      .get(item.version_title)!
      .get(item.visitor_id)!;

    // Format reader name like in the UI, using version-specific index
    const readerName = item.reader_name || `Reader #${versionVisitorIndex}`;

    // Format location like in the UI
    const location = `${item.city ? `${item.city}, ` : ""}${item.country}`;

    const payload: Record<string, string | number> = {
      "Story Name": decodeHtmlEntities(item.story_title),
      "Version Name": decodeHtmlEntities(item.version_title),
      "Created At": formatDate(item.timestamp),
      Reader: readerName,
      "Session Index": item.session_index,
      "Session Started": formatDate(item.timestamp),
      Duration: item.duration,
      Progress: progress,
      "Clicked CTA": item.click_cta ? "Yes" : "No",
      Location: location,
      Device: item.device ?? "",
      "Traffic Type": item.traffic_type ?? "",
      "Bot Signature": item.bot_signature ?? "",
      "Reader Email": item.reader_email ?? "",
      "Reader Position": item.reader_position ?? "",
    };

    return payload;
  });
};

function escapeCsvValue(value: string, columnDelimiter: string) {
  // Escape quotes by doubling them, and wrap in quotes if value contains special chars
  if (value == null) return "";
  const needsQuoting =
    /["\n\r,]/.test(value) || value.includes(columnDelimiter);
  let escaped = value.replace(/"/g, '""');
  if (needsQuoting) {
    escaped = `"${escaped}"`;
  }
  return escaped;
}

const parseDataToCsv = (
  data: CsvType[],
  columnDelimiter = ",",
  lineDelimiter = "\n",
) => {
  if (!data || data.length === 0) return "";

  const rows = mapData(data);
  const keys = HEADERS;

  let result = "";
  result += keys.join(columnDelimiter);
  result += lineDelimiter;

  rows.forEach((item) => {
    result += keys
      .map((key) => escapeCsvValue(String(item[key] ?? ""), columnDelimiter))
      .join(columnDelimiter);
    result += lineDelimiter;
  });

  return result;
};

const formatDate = (date: string) => {
  return moment.utc(date).local().format("DD/MM/YYYY h:mm A");
};

export default parseDataToCsv;
